import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminMarketplaceAPI } from '../services/marketplaceAPI';
import { getImageUrl } from '../utils/imageUtils';
import '../styles/AdminDashboard.css';

// Add Product Form Component
const AddProductForm = ({ onProductAdded }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'medicine',
    price: '',
    stock: '',
    manufacturer: '',
    dosage: '',
    requirements: '',
    testType: '',
    preparationInstructions: '',
    reportDeliveryTime: '',
    sampleType: '',
    labOptions: []
  });
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    console.log('File selected:', file);
    if (file) {
      setImageFile(file);
      setImageUrl(''); // Clear URL when file is selected
      setImageError(false);
      setImageLoading(true);
      
      // Create preview from file
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log('FileReader loaded, result length:', reader.result?.length);
        setImagePreview(reader.result);
        setImageLoading(false);
      };
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        setImageError(true);
        setImagePreview(null);
        setImageLoading(false);
      };
      reader.readAsDataURL(file);
    } else {
      setImageFile(null);
      setImagePreview(null);
      setImageLoading(false);
    }
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    console.log('Image URL changed:', url);
    setImageUrl(url);
    if (url && url.trim() !== '') {
      setImageFile(null); // Clear file when URL is entered
      setImageError(false);
      setImageLoading(true);
      setImagePreview(url); // Set preview immediately
      
      // Validate URL by trying to load image
      const img = new Image();
      img.onload = () => {
        console.log('Image loaded successfully from URL:', url);
        setImageLoading(false);
        setImageError(false);
        setImagePreview(url);
      };
      img.onerror = (error) => {
        console.error('Image failed to load from URL:', url, error);
        setImageLoading(false);
        setImageError(true);
        // Keep preview even if validation fails - user might want to submit anyway
        setImagePreview(url);
      };
      img.src = url;
    } else {
      setImagePreview(null);
      setImageLoading(false);
      setImageError(false);
    }
  };

  const handleLabOptionChange = (index, field, value) => {
    const updatedLabOptions = [...formData.labOptions];
    updatedLabOptions[index] = {
      ...updatedLabOptions[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      labOptions: updatedLabOptions
    }));
  };

  const addLabOption = () => {
    setFormData(prev => ({
      ...prev,
      labOptions: [...prev.labOptions, { labName: '', price: '' }]
    }));
  };

  const removeLabOption = (index) => {
    setFormData(prev => ({
      ...prev,
      labOptions: prev.labOptions.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formDataToSend = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (key === 'labOptions') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Add image if selected (file takes priority over URL)
      if (imageFile) {
        formDataToSend.append('image', imageFile);
      } else if (imageUrl) {
        formDataToSend.append('imageUrl', imageUrl);
      }

      const response = await adminMarketplaceAPI.createProduct(formDataToSend);
      
      if (response.success) {
        setSuccess('Product created successfully!');
        // Reset form
        setFormData({
          name: '',
          description: '',
          category: 'medicine',
          price: '',
          stock: '',
          manufacturer: '',
          dosage: '',
          requirements: '',
          testType: '',
          preparationInstructions: '',
          reportDeliveryTime: '',
          sampleType: '',
          labOptions: []
        });
        setImageFile(null);
        setImageUrl('');
        setImagePreview(null);
        setImageError(false);
        setImageLoading(false);
        
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
        
        // Call the callback to refresh products
        setTimeout(() => {
          onProductAdded();
        }, 1500);
      } else {
        setError(response.message || 'Failed to create product');
      }
    } catch (err) {
      console.error('Error creating product:', err);
      setError(err.response?.data?.message || 'Error creating product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-product-form" style={{ maxWidth: '600px', margin: '0 auto' }}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <h4 style={{ marginBottom: '1.5rem', color: '#374151' }}>Add New Product</h4>
      
      {error && (
        <div style={{ 
          background: '#fee2e2', 
          border: '1px solid #fecaca', 
          color: '#dc2626', 
          padding: '0.75rem', 
          borderRadius: '0.5rem', 
          marginBottom: '1rem' 
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ 
          background: '#dcfce7', 
          border: '1px solid #bbf7d0', 
          color: '#166534', 
          padding: '0.75rem', 
          borderRadius: '0.5rem', 
          marginBottom: '1rem' 
        }}>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
            Product Name *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
            placeholder="Enter product name"
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
            Description *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required
            rows={3}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              resize: 'vertical'
            }}
            placeholder="Enter product description"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Category *
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            >
              <option value="medicine">Medicine</option>
              <option value="lab-test">Lab Test</option>
            </select>
          </div>

          {formData.category === 'medicine' && (
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                Price (৳) *
              </label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                required
                min="0"
                step="0.01"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
                placeholder="0.00"
              />
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          {formData.category === 'medicine' && (
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                Stock Quantity *
              </label>
              <input
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleInputChange}
                required
                min="0"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
                placeholder="Enter stock quantity"
              />
            </div>
          )}

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Manufacturer
            </label>
            <input
              type="text"
              name="manufacturer"
              value={formData.manufacturer}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
              placeholder="Enter manufacturer name"
            />
          </div>
        </div>

        {formData.category === 'medicine' && (
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Dosage (for medicines)
            </label>
            <input
              type="text"
              name="dosage"
              value={formData.dosage}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
              placeholder="e.g., 500mg, 10ml"
            />
          </div>
        )}

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
            Requirements
          </label>
          <input
            type="text"
            name="requirements"
            value={formData.requirements}
            onChange={handleInputChange}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
            placeholder="e.g., Prescription required"
          />
        </div>

        {/* Lab Test Specific Fields */}
        {formData.category === 'lab-test' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                  Test Type
                </label>
                <input
                  type="text"
                  name="testType"
                  value={formData.testType}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                  placeholder="e.g., Blood Test, Urine Test"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                  Sample Type
                </label>
                <input
                  type="text"
                  name="sampleType"
                  value={formData.sampleType}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                  placeholder="e.g., Blood, Urine, Saliva"
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                Preparation Instructions
              </label>
              <textarea
                name="preparationInstructions"
                value={formData.preparationInstructions}
                onChange={handleInputChange}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  resize: 'vertical'
                }}
                placeholder="e.g., Fasting required for 8 hours"
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                Report Delivery Time
              </label>
              <input
                type="text"
                name="reportDeliveryTime"
                value={formData.reportDeliveryTime}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
                placeholder="e.g., 24-48 hours"
              />
            </div>

            {/* Lab Options Section */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: '500', color: '#374151' }}>
                  Lab Options
                </label>
                <button
                  type="button"
                  onClick={addLabOption}
                  className="admin-btn admin-btn-primary"
                  style={{
                    fontSize: '0.875rem'
                  }}
                >
                  + Add Lab Option
                </button>
              </div>
              
              {formData.labOptions.length === 0 ? (
                <div style={{
                  padding: '1rem',
                  background: '#f9fafb',
                  border: '2px dashed #d1d5db',
                  borderRadius: '0.5rem',
                  textAlign: 'center',
                  color: '#6b7280'
                }}>
                  No lab options added yet. Click "Add Lab Option" to add lab choices with different prices.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {formData.labOptions.map((option, index) => (
                    <div key={index} style={{
                      padding: '1rem',
                      background: '#f8fafc',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.5rem'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                          Lab Option {index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeLabOption(index)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            background: '#6B6B6B',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            cursor: 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
                            Lab Name
                          </label>
                          <input
                            type="text"
                            value={option.labName}
                            onChange={(e) => handleLabOptionChange(index, 'labName', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem'
                            }}
                            placeholder="e.g., Square Hospital"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
                            Price (৳)
                          </label>
                          <input
                            type="number"
                            value={option.price}
                            onChange={(e) => handleLabOptionChange(index, 'price', e.target.value)}
                            min="0"
                            step="0.01"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem'
                            }}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
            Product Image
          </label>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {/* File Upload Option */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
                Option 1: Choose File
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            
            {/* URL Input Option */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
                Option 2: Image URL
              </label>
              <input
                type="url"
                value={imageUrl}
                onChange={handleImageUrlChange}
                placeholder="https://example.com/image.jpg"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: imageError ? '1px solid #dc2626' : '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
              />
              {imageError && imageUrl && (
                <p style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem' }}>
                  ⚠️ Invalid image URL or image cannot be loaded
                </p>
              )}
            </div>
          </div>
          
          {/* Image Preview - Always show if there's any image data */}
          <div style={{ marginTop: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Image Preview
              {imagePreview && (
                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '400', marginLeft: '0.5rem' }}>
                  ({imageFile ? 'File Upload' : 'URL'})
                </span>
              )}
            </label>
            
            {/* Debug Info */}
            {(imageFile || imageUrl) && (
              <div style={{ 
                fontSize: '0.75rem', 
                color: '#6b7280', 
                marginBottom: '0.5rem',
                padding: '0.5rem',
                background: '#f3f4f6',
                borderRadius: '0.25rem'
              }}>
                {imageFile && <p>File: {imageFile.name} ({(imageFile.size / 1024).toFixed(2)} KB)</p>}
                {imageUrl && <p style={{ wordBreak: 'break-all', marginTop: imageFile ? '0.25rem' : 0 }}>URL: {imageUrl}</p>}
                {imagePreview && <p style={{ marginTop: '0.25rem', color: '#059669' }}>✓ Preview ready</p>}
              </div>
            )}
            
            <div style={{
              width: '100%',
              maxWidth: '400px',
              height: '250px',
              border: imageError ? '2px solid #dc2626' : '2px solid #e5e7eb',
              borderRadius: '0.5rem',
              overflow: 'hidden',
              position: 'relative',
              background: '#f9fafb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {imageLoading ? (
                <div style={{ textAlign: 'center', color: '#6b7280' }}>
                  <div style={{ 
                    width: '40px', 
                    height: '40px', 
                    border: '3px solid #e5e7eb',
                    borderTop: '3px solid #2C2C2C',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 0.5rem'
                  }}></div>
                  <p style={{ fontSize: '0.875rem' }}>Loading image...</p>
                </div>
              ) : imagePreview ? (
                <>
                  <img
                    key={imagePreview} // Force re-render when preview changes
                    src={imagePreview}
                    alt="Product preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      display: 'block',
                      maxWidth: '100%',
                      maxHeight: '100%'
                    }}
                    onError={(e) => {
                      console.error('Image preview error:', e);
                      setImageError(true);
                    }}
                    onLoad={(e) => {
                      console.log('Image preview loaded successfully');
                      setImageLoading(false);
                      setImageError(false);
                    }}
                  />
                  {imageError && (
                    <div style={{
                      position: 'absolute',
                      top: '0.5rem',
                      right: '0.5rem',
                      background: 'rgba(220, 38, 38, 0.9)',
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.25rem',
                      fontSize: '0.75rem'
                    }}>
                      ⚠️ Load Error
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
                  <svg style={{ width: '48px', height: '48px', margin: '0 auto 0.5rem', opacity: 0.5 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p style={{ fontSize: '0.875rem' }}>No image selected</p>
                  <p style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>Upload a file or enter a URL</p>
                </div>
              )}
            </div>
          </div>
          
          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
            Optional: Upload an image file or provide an image URL (File upload takes priority)
          </p>
        </div>

        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
          <button
            type="submit"
            disabled={loading}
            className="admin-btn admin-btn-primary"
            style={{
              flex: 1,
              fontSize: '0.875rem'
            }}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Product'}
          </button>
          
          <button
            type="button"
            onClick={onProductAdded}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.875rem'
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// Edit Product Form Component
const EditProductForm = ({ product, onProductUpdated, onCancel }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    category: product?.category || 'medicine',
    price: product?.price || '',
    stock: product?.stock || '',
    manufacturer: product?.manufacturer || '',
    dosage: product?.dosage || '',
    requirements: product?.requirements || '',
    testType: product?.testType || '',
    preparationInstructions: product?.preparationInstructions || '',
    reportDeliveryTime: product?.reportDeliveryTime || '',
    sampleType: product?.sampleType || '',
    labOptions: product?.labOptions || [],
    isActive: product?.isActive !== undefined ? product.isActive : true
  });
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(product?.image || '');
  const [imagePreview, setImagePreview] = useState(product?.image ? getImageUrl(product.image) : null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImageUrl('');
      setImageError(false);
      setImageLoading(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setImageLoading(false);
      };
      reader.onerror = () => {
        setImageError(true);
        setImagePreview(null);
        setImageLoading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setImageUrl(url);
    if (url) {
      setImageFile(null);
      setImageError(false);
      setImageLoading(true);
      setImagePreview(url);
      const img = new Image();
      img.onload = () => {
        setImageLoading(false);
        setImageError(false);
        setImagePreview(url);
      };
      img.onerror = () => {
        setImageLoading(false);
        setImageError(true);
        setImagePreview(null);
      };
      img.src = url;
    } else {
      setImagePreview(null);
    }
  };

  const handleLabOptionChange = (index, field, value) => {
    const updatedLabOptions = [...formData.labOptions];
    updatedLabOptions[index] = {
      ...updatedLabOptions[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      labOptions: updatedLabOptions
    }));
  };

  const addLabOption = () => {
    setFormData(prev => ({
      ...prev,
      labOptions: [...prev.labOptions, { labName: '', price: '' }]
    }));
  };

  const removeLabOption = (index) => {
    setFormData(prev => ({
      ...prev,
      labOptions: prev.labOptions.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formDataToSend = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (key === 'labOptions') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      if (imageFile) {
        formDataToSend.append('image', imageFile);
      } else if (imageUrl && imageUrl !== product?.image) {
        formDataToSend.append('imageUrl', imageUrl);
      }

      const response = await adminMarketplaceAPI.updateProduct(product._id, formDataToSend);
      
      if (response.success) {
        setSuccess('Product updated successfully!');
        setTimeout(() => {
          onProductUpdated();
          onCancel();
        }, 1500);
      } else {
        setError(response.message || 'Failed to update product');
      }
    } catch (err) {
      console.error('Error updating product:', err);
      setError(err.response?.data?.message || 'Error updating product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="edit-product-form" style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: 'rgba(0, 0, 0, 0.5)', 
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      overflowY: 'auto'
    }}>
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        padding: '2rem', 
        maxWidth: '700px', 
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700', color: '#1f2937' }}>Edit Product</h2>
          <button
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0.25rem 0.5rem'
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div style={{ padding: '0.75rem', background: '#fee2e2', color: '#dc2626', borderRadius: '6px', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ padding: '0.75rem', background: '#d1fae5', color: '#059669', borderRadius: '6px', marginBottom: '1rem' }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Reuse the same form fields as AddProductForm but with pre-filled values */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Product Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Category *
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            >
              <option value="medicine">Medicine</option>
              <option value="lab-test">Lab Test</option>
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                Price (৳) *
              </label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                required
                min="0"
                step="0.01"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                Stock *
              </label>
              <input
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleInputChange}
                required
                min="0"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: '500', color: '#374151' }}>Active Product</span>
            </label>
          </div>

          {/* Image upload section - same as AddProductForm */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Product Image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ marginBottom: '0.5rem' }}
            />
            <input
              type="text"
              placeholder="Or enter image URL"
              value={imageUrl}
              onChange={handleImageUrlChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                marginTop: '0.5rem'
              }}
            />
            {imagePreview && (
              <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}
                  onError={() => setImageError(true)}
                />
              </div>
            )}
          </div>

          {/* Category-specific fields */}
          {formData.category === 'medicine' && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                  Manufacturer
                </label>
                <input
                  type="text"
                  name="manufacturer"
                  value={formData.manufacturer}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                  Dosage
                </label>
                <input
                  type="text"
                  name="dosage"
                  value={formData.dosage}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
            </>
          )}

          {formData.category === 'lab-test' && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                  Test Type
                </label>
                <input
                  type="text"
                  name="testType"
                  value={formData.testType}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                  Requirements
                </label>
                <textarea
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleInputChange}
                  rows="2"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                  Preparation Instructions
                </label>
                <textarea
                  name="preparationInstructions"
                  value={formData.preparationInstructions}
                  onChange={handleInputChange}
                  rows="2"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                    Report Delivery Time
                  </label>
                  <input
                    type="text"
                    name="reportDeliveryTime"
                    value={formData.reportDeliveryTime}
                    onChange={handleInputChange}
                    placeholder="e.g., 24-48 hours"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                    Sample Type
                  </label>
                  <input
                    type="text"
                    name="sampleType"
                    value={formData.sampleType}
                    onChange={handleInputChange}
                    placeholder="e.g., Blood, Urine"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: loading ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Updating...' : 'Update Product'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminMarketplace = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);

  // Helper function to determine order type (medicine or lab-test)
  const getOrderType = (order) => {
    if (!order.items || order.items.length === 0) return 'medicine';
    const firstItem = order.items[0];
    const productCategory = firstItem.productId?.category || firstItem.category;
    return productCategory === 'lab-test' ? 'lab-test' : 'medicine';
  };

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchStats();
    } else if (activeTab === 'products') {
      fetchProducts();
    } else if (activeTab === 'orders') {
      fetchOrders();
    }
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching admin marketplace stats...');
      const response = await adminMarketplaceAPI.getMarketplaceStats();
      console.log('Stats response:', response);
      if (response.success) {
        setStats(response.data);
      } else {
        setError('Failed to load statistics');
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      if (err.response?.status === 403) {
        setError('Access denied: Admin privileges required');
      } else if (err.response?.status === 401) {
        setError('Authentication required: Please log in as admin');
      } else {
        setError('Error fetching statistics');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching admin marketplace products...');
      const response = await adminMarketplaceAPI.getAllProducts();
      console.log('Products response:', response);
      if (response.success) {
        setProducts(response.data.products || []);
      } else {
        setError('Failed to load products');
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      if (err.response?.status === 403) {
        setError('Access denied: Admin privileges required');
      } else if (err.response?.status === 401) {
        setError('Authentication required: Please log in as admin');
      } else {
        setError('Error fetching products');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminMarketplaceAPI.getAllOrders();
      console.log('Orders response:', response);
      if (response.success) {
        setOrders(response.data.orders || []);
      } else {
        setError('Failed to load orders');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Error fetching orders');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit product
  const handleEditProduct = (product) => {
    setEditingProduct(product);
  };

  // Handle product updated
  const handleProductUpdated = () => {
    fetchProducts();
    if (activeTab === 'overview') {
      fetchStats();
    }
  };

  // Handle delete product
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const response = await adminMarketplaceAPI.deleteProduct(productId);
      if (response.success) {
        // Remove from local list
        setProducts(prev => prev.filter(p => p._id !== productId));
        setSuccess('Product deleted successfully');
        setTimeout(() => setSuccess(''), 3000);
        // Refresh stats if on overview
        if (activeTab === 'overview') {
          fetchStats();
        }
      } else {
        setError(response.message || 'Failed to delete product');
      }
    } catch (err) {
      console.error('Error deleting product:', err);
      if (err.response?.status === 403) {
        setError('Access denied: Admin privileges required');
      } else if (err.response?.status === 401) {
        setError('Authentication required: Please log in as admin');
      } else {
        setError('Error deleting product');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle order status update
  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const response = await adminMarketplaceAPI.updateOrderStatus(orderId, { status: newStatus });
      
      if (response.success) {
        // Update the orders list locally
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order._id === orderId 
              ? { ...order, status: newStatus }
              : order
          )
        );
        setSuccess(`Order status updated to ${newStatus.charAt(0).toUpperCase() + newStatus.slice(1)}`);
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to update order status');
      }
    } catch (err) {
      console.error('Error updating order status:', err);
      if (err.response?.status === 403) {
        setError('Access denied: Admin privileges required');
      } else if (err.response?.status === 401) {
        setError('Authentication required: Please log in as admin');
      } else {
        setError('Error updating order status');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle view order details
  const handleViewOrderDetails = async (orderId) => {
    if (!orderId) {
      console.error('Order ID is missing');
      return;
    }

    console.log('=== handleViewOrderDetails called ===');
    console.log('Order ID received:', orderId);
    console.log('Order ID type:', typeof orderId);

    setLoadingOrderDetails(true);
    setSelectedOrder(orderId);
    setOrderDetails(null);

    try {
      console.log('Calling adminMarketplaceAPI.getOrderById with:', orderId);
      const response = await adminMarketplaceAPI.getOrderById(orderId);
      console.log('Response received:', response);
      if (response.success) {
        setOrderDetails(response.data);
      } else {
        setError('Failed to load order details');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      console.error('Error fetching order details:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      setError('Error loading order details. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  // Close order details modal
  const handleCloseOrderDetails = () => {
    setSelectedOrder(null);
    setOrderDetails(null);
  };

  // useEffect to fetch data based on active tab
  useEffect(() => {
    if (activeTab === 'overview') {
      fetchStats();
      fetchProducts();
      fetchOrders();
    } else if (activeTab === 'products') {
      fetchProducts();
    } else if (activeTab === 'orders') {
      fetchOrders();
    }
  }, [activeTab]);

  return (
    <div className="admin-marketplace">
      <div className="marketplace-header" style={{ marginBottom: '2rem' }}>
        <h3>Marketplace Management</h3>
        <p>Manage products, orders, and marketplace statistics</p>
      </div>

      {/* Navigation Tabs */}
      <div className="admin-tabs" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-tabs-list">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'products', label: 'Products' },
            { id: 'orders', label: 'Orders' },
            { id: 'add-product', label: 'Add Product' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`admin-tab-button ${activeTab === tab.id ? 'active' : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ 
          background: '#fee2e2', 
          border: '1px solid #fecaca', 
          color: '#dc2626', 
          padding: '0.75rem', 
          borderRadius: '0.5rem', 
          marginBottom: '1rem' 
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ 
          background: '#dcfce7', 
          border: '1px solid #bbf7d0', 
          color: '#166534', 
          padding: '0.75rem', 
          borderRadius: '0.5rem', 
          marginBottom: '1rem' 
        }}>
          {success}
        </div>
      )}

      {/* Content based on active tab */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <>
          {activeTab === 'overview' && stats && (
            <div className="marketplace-overview">
              <div className="admin-stats-grid">
                <div className="admin-stat-card">
                  <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <p className="admin-stat-card-label">Total Products</p>
                  <h3 className="admin-stat-card-value">{stats.products.total}</h3>
                  <div className="admin-stat-card-footer">
                    <strong style={{ color: 'var(--admin-success)' }}>{stats.products.active}</strong> active
                  </div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="admin-stat-card-label">Medicines</p>
                  <h3 className="admin-stat-card-value">{stats.products.medicines}</h3>
                  <div className="admin-stat-card-footer">
                    Available products
                  </div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <p className="admin-stat-card-label">Lab Tests</p>
                  <h3 className="admin-stat-card-value">{stats.products.labTests}</h3>
                  <div className="admin-stat-card-footer">
                    Available tests
                  </div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                  </div>
                  <p className="admin-stat-card-label">Total Orders</p>
                  <h3 className="admin-stat-card-value">{stats.orders.total}</h3>
                  <div className="admin-stat-card-footer">
                    <strong style={{ color: 'var(--admin-warning)' }}>{stats.orders.pending}</strong> pending
                  </div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="admin-stat-card-label">Revenue</p>
                  <h3 className="admin-stat-card-value">৳{stats.revenue.total.toFixed(2)}</h3>
                  <div className="admin-stat-card-footer">
                    Total marketplace revenue
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setActiveTab('products')}
                  className="admin-btn admin-btn-primary"
                >
                  Manage Products
                </button>
                <button
                  onClick={() => setActiveTab('orders')}
                  className="admin-btn admin-btn-primary"
                >
                  Manage Orders
                </button>
              </div>
            </div>
          )}

          {activeTab === 'products' && (
            <div className="products-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4>Recent Products</h4>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => setActiveTab('add-product')}
                    className="admin-btn admin-btn-primary"
                    style={{
                      fontSize: '0.875rem'
                    }}
                  >
                    Add Product
                  </button>
                </div>
              </div>
              {products.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem', 
                  background: '#f9fafb', 
                  borderRadius: '12px',
                  border: '2px dashed #e5e7eb'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📦</div>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '1.25rem' }}>No Products Found</h4>
                  <p style={{ margin: '0 0 1rem 0', color: '#6b7280' }}>Get started by adding your first product</p>
                  <button 
                    onClick={() => setActiveTab('add-product')} 
                    style={{ 
                      padding: '0.75rem 1.5rem',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.875rem'
                    }}
                  >
                    Add Your First Product
                  </button>
                </div>
              ) : (
                <div className="table-container" style={{ 
                  background: 'white', 
                  borderRadius: '12px', 
                  overflow: 'hidden',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  border: '1px solid #e5e7eb'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'linear-gradient(135deg, #f5f5f5 0%, #e5e5e5 100%)' }}>
                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #d3d3d3', fontWeight: '700', color: '#2C2C2C', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Image</th>
                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #d3d3d3', fontWeight: '700', color: '#2C2C2C', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Name</th>
                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #d3d3d3', fontWeight: '700', color: '#2C2C2C', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Category</th>
                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #d3d3d3', fontWeight: '700', color: '#2C2C2C', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Price</th>
                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #d3d3d3', fontWeight: '700', color: '#2C2C2C', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Stock</th>
                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #d3d3d3', fontWeight: '700', color: '#2C2C2C', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Status</th>
                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #d3d3d3', fontWeight: '700', color: '#2C2C2C', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.slice(0, 5).map(product => {
                        const imageUrl = getImageUrl(product.image);
                        console.log('Image URL for', product.name, ':', imageUrl);
                        
                        return (
                        <tr 
                          key={product._id}
                          style={{ 
                            transition: 'all 0.2s',
                            borderBottom: '1px solid #e5e7eb',
                            background: 'white'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <td style={{ padding: '0.75rem', border: 'none' }}>
                            <div style={{
                              width: '60px',
                              height: '60px',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              background: 'linear-gradient(135deg, #f5f5f5 0%, #e5e5e5 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid #d3d3d3',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                            }}>
                              <img
                                key={`${product._id}-${imageUrl}`}
                                src={imageUrl}
                                alt={product.name}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'block'
                                }}
                                onError={(e) => {
                                  console.error('Image failed to load:', imageUrl, 'for product:', product.name);
                                  e.target.src = '/placeholder-product.png';
                                }}
                                onLoad={() => {
                                  console.log('Image loaded successfully:', imageUrl, 'for product:', product.name);
                                }}
                              />
                            </div>
                          </td>
                          <td style={{ padding: '0.75rem', border: 'none', fontWeight: '500', color: '#1f2937' }}>{product.name}</td>
                          <td style={{ padding: '0.75rem', border: 'none', whiteSpace: 'nowrap' }}>
                            <span style={{ 
                              padding: '0.375rem 0.625rem', 
                              borderRadius: '6px', 
                              fontSize: '0.6875rem',
                              background: product.category === 'medicine' 
                                ? 'linear-gradient(135deg, #f5f5f5 0%, #e5e5e5 100%)' 
                                : 'linear-gradient(135deg, #efefef 0%, #d3d3d3 100%)',
                              color: '#2C2C2C',
                              border: '1px solid #d3d3d3',
                              fontWeight: '600',
                              display: 'inline-block',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '120px'
                            }}>
                              {product.category === 'medicine' ? '💊 Medicine' : '🧪 Lab Test'}
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem', border: 'none', fontWeight: '600', color: '#dc2626' }}>৳{product.price}</td>
                          <td style={{ padding: '0.75rem', border: 'none', fontWeight: '500', color: '#374151' }}>{product.stock}</td>
                          <td style={{ padding: '0.75rem', border: 'none', whiteSpace: 'nowrap' }}>
                            <span style={{ 
                              padding: '0.375rem 0.625rem', 
                              borderRadius: '6px', 
                              fontSize: '0.6875rem',
                              background: product.isActive 
                                ? 'linear-gradient(135deg, #f5f5f5 0%, #e5e5e5 100%)' 
                                : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                              color: product.isActive ? '#2C2C2C' : '#dc2626',
                              border: product.isActive ? '1px solid #d3d3d3' : '1px solid #fca5a5',
                              fontWeight: '600',
                              display: 'inline-block',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100px'
                            }}>
                              {product.isActive ? '✓ Active' : '○ Inactive'}
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem', border: 'none', whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'nowrap' }}>
                              <button
                                onClick={() => handleEditProduct(product)}
                                style={{
                                  padding: '0.5rem 0.875rem',
                                  background: 'linear-gradient(135deg, #4a4a4a 0%, #2c2c2c 100%)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  transition: 'all 0.2s',
                                  whiteSpace: 'nowrap',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                                }}
                                onMouseOver={(e) => {
                                  e.target.style.background = 'linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 100%)';
                                  e.target.style.transform = 'translateY(-1px)';
                                  e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.3)';
                                }}
                                onMouseOut={(e) => {
                                  e.target.style.background = 'linear-gradient(135deg, #4a4a4a 0%, #2c2c2c 100%)';
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                                }}
                              >
                                ✏️ Edit
                              </button>
                              <button
                                onClick={() => handleDeleteProduct(product._id)}
                                style={{
                                  padding: '0.5rem 0.875rem',
                                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  transition: 'all 0.2s',
                                  whiteSpace: 'nowrap',
                                  boxShadow: '0 2px 4px rgba(220, 38, 38, 0.3)'
                                }}
                                onMouseOver={(e) => {
                                  e.target.style.background = 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)';
                                  e.target.style.transform = 'translateY(-1px)';
                                  e.target.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.4)';
                                }}
                                onMouseOut={(e) => {
                                  e.target.style.background = 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)';
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.3)';
                                }}
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'orders' && (
            <div className="orders-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4>Recent Orders</h4>
              </div>
              {orders.length === 0 ? (
                <p>No orders found.</p>
              ) : (
                <div className="table-container">
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #e5e7eb' }}>Order ID</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #e5e7eb' }}>Customer</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #e5e7eb' }}>Total</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #e5e7eb' }}>Status</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #e5e7eb' }}>Date</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #e5e7eb' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.slice(0, 5).map(order => (
                        <tr key={order._id}>
                          <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb', fontFamily: 'monospace' }}>{order.orderId}</td>
                          <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>{order.userId?.fullName || order.userId?.name || 'N/A'}</td>
                          <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>৳{order.totalAmount}</td>
                          <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>
                            <select
                              value={order.status}
                              onChange={(e) => handleStatusUpdate(order._id, e.target.value)}
                              style={{
                                padding: '0.5rem',
                                border: '2px solid #E5E5E5',
                                borderRadius: '0.5rem',
                                background: 'white',
                                fontSize: '0.875rem',
                                cursor: 'pointer',
                                minWidth: '180px',
                                fontWeight: '500',
                                transition: 'border-color 0.2s ease',
                                outline: 'none',
                                color: '#2C2C2C'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#6B6B6B'}
                              onBlur={(e) => e.target.style.borderColor = '#E5E5E5'}
                            >
                              {getOrderType(order) === 'medicine' ? (
                                // Medicine order statuses only
                                <>
                                  <option value="pending">⏳ Pending</option>
                                  <option value="confirmed">✅ Confirmed</option>
                                  <option value="processing">⚡ Processing</option>
                                  <option value="shipped">🚚 Shipped</option>
                                  <option value="delivered">✨ Delivered</option>
                                  <option value="cancelled">❌ Cancelled</option>
                                </>
                              ) : (
                                // Lab test specific statuses only
                                <>
                                  <option value="received-request">📋 Received Request</option>
                                  <option value="processing-request">⚡ Processing Request</option>
                                  <option value="sent-for-sample-collection">🚚 Sample Collection</option>
                                  <option value="sample-collected">🧪 Sample Collected</option>
                                  <option value="report-delivered">📄 Report Delivered</option>
                                  <option value="cancelled">❌ Cancelled</option>
                                </>
                              )}
                            </select>
                          </td>
                          <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>
                            {new Date(order.orderDate).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>
                            <button
                              onClick={() => handleViewOrderDetails(order.orderId || order._id)}
                              className="admin-btn admin-btn-primary admin-btn-sm"
                              style={{
                                whiteSpace: 'nowrap'
                              }}
                            >
                              👁️ View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'add-product' && (
            <AddProductForm onProductAdded={() => {
              setActiveTab('products');
              fetchProducts();
            }} />
          )}
        </>
      )}

      {/* Edit Product Modal */}
      {editingProduct && (
        <EditProductForm
          product={editingProduct}
          onProductUpdated={handleProductUpdated}
          onCancel={() => setEditingProduct(null)}
        />
      )}

      {/* Order Details Modal */}
      {selectedOrder && (
        <OrderDetailsModal
          orderId={selectedOrder}
          order={orderDetails}
          loading={loadingOrderDetails}
          onClose={handleCloseOrderDetails}
          onRefresh={() => {
            fetchOrders();
            if (activeTab === 'overview') {
              fetchStats();
            }
          }}
        />
      )}
    </div>
  );
};

// Order Details Modal Component
const OrderDetailsModal = ({ orderId, order, loading, onClose, onRefresh }) => {
  const getStatusColor = (status) => {
    const statusColors = {
      'pending': { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
      'confirmed': { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
      'processing': { bg: '#e0e7ff', text: '#3730a3', border: '#a5b4fc' },
      'shipped': { bg: '#f3e8ff', text: '#6b21a8', border: '#c084fc' },
      'delivered': { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
      'cancelled': { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
      'received-request': { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
      'processing-request': { bg: '#e0e7ff', text: '#3730a3', border: '#a5b4fc' },
      'sent-for-sample-collection': { bg: '#f3e8ff', text: '#6b21a8', border: '#c084fc' },
      'sample-collected': { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
      'report-delivered': { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' }
    };
    return statusColors[status] || { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: 'rgba(0, 0, 0, 0.5)', 
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      overflowY: 'auto'
    }} onClick={onClose}>
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        padding: '2rem', 
        maxWidth: '900px', 
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700', color: '#1f2937' }}>Order Details</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#f3f4f6';
              e.target.style.color = '#374151';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'none';
              e.target.style.color = '#6b7280';
            }}
          >
            ×
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 1rem', width: '40px', height: '40px', border: '4px solid #f3f4f6', borderTop: '4px solid #2C2C2C', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            <p style={{ color: '#6b7280' }}>Loading order details...</p>
          </div>
        ) : order ? (
          <div>
            {/* Order Header */}
            <div style={{ 
              background: 'linear-gradient(135deg, #f5f5f5 0%, #e5e5e5 100%)',
              padding: '1.5rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              border: '1px solid #d3d3d3'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div>
                  <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Order ID</p>
                  <p style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#2C2C2C', fontFamily: 'monospace' }}>{order.orderId || order._id}</p>
                </div>
                <div>
                  <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Order Status</p>
                  <span style={{ 
                    padding: '0.375rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    background: getStatusColor(order.status).bg,
                    color: getStatusColor(order.status).text,
                    border: `1px solid ${getStatusColor(order.status).border}`,
                    display: 'inline-block'
                  }}>
                    {order.status}
                  </span>
                </div>
                <div>
                  <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Order Date</p>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '500', color: '#2C2C2C' }}>{formatDate(order.orderDate || order.createdAt)}</p>
                </div>
                <div>
                  <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Total Amount</p>
                  <p style={{ margin: 0, fontSize: '1.125rem', fontWeight: '700', color: '#dc2626' }}>৳{order.totalAmount?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </div>

            {/* Patient Information */}
            <div style={{ 
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>Patient Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div>
                  <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Patient Name</p>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '500', color: '#2C2C2C' }}>{order.userId?.fullName || order.userId?.name || 'N/A'}</p>
                </div>
                <div>
                  <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Patient ID</p>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '500', color: '#2C2C2C', fontFamily: 'monospace' }}>{order.userId?._id || 'N/A'}</p>
                </div>
                <div>
                  <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Email</p>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '500', color: '#2C2C2C' }}>{order.userId?.email || 'N/A'}</p>
                </div>
                <div>
                  <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Phone</p>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '500', color: '#2C2C2C' }}>{order.userId?.phone || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div style={{ 
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>Order Items</h3>
              {order.items && order.items.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {order.items.map((item, index) => {
                    const itemStatus = item.status || order.status;
                    const statusColor = getStatusColor(itemStatus);
                    return (
                      <div 
                        key={index}
                        style={{
                          padding: '1rem',
                          background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#d3d3d3';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                          {/* Product Image */}
                          <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            background: 'linear-gradient(135deg, #f5f5f5 0%, #e5e5e5 100%)',
                            border: '1px solid #d3d3d3',
                            flexShrink: 0
                          }}>
                            <img
                              src={getImageUrl(item.productId?.image)}
                              alt={item.name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                e.target.src = '/placeholder-product.png';
                              }}
                            />
                          </div>

                          {/* Product Details */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                              <div style={{ flex: 1 }}>
                                <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                                  {item.name || item.productId?.name || 'Unknown Product'}
                                </h4>
                                <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.75rem', color: '#6b7280' }}>
                                  {item.category === 'medicine' ? '💊 Medicine' : '🧪 Lab Test'}
                                </p>
                              </div>
                              <span style={{ 
                                padding: '0.375rem 0.75rem',
                                borderRadius: '6px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                background: statusColor.bg,
                                color: statusColor.text,
                                border: `1px solid ${statusColor.border}`,
                                whiteSpace: 'nowrap',
                                marginLeft: '1rem'
                              }}>
                                {itemStatus}
                              </span>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem', fontSize: '0.875rem' }}>
                              <div>
                                <span style={{ color: '#6b7280', fontWeight: '500' }}>Quantity: </span>
                                <span style={{ color: '#2C2C2C', fontWeight: '600' }}>{item.quantity}</span>
                              </div>
                              <div>
                                <span style={{ color: '#6b7280', fontWeight: '500' }}>Unit Price: </span>
                                <span style={{ color: '#2C2C2C', fontWeight: '600' }}>৳{item.price?.toFixed(2) || '0.00'}</span>
                              </div>
                              <div>
                                <span style={{ color: '#6b7280', fontWeight: '500' }}>Subtotal: </span>
                                <span style={{ color: '#dc2626', fontWeight: '700' }}>৳{item.total?.toFixed(2) || (item.price * item.quantity).toFixed(2)}</span>
                              </div>
                            </div>

                            {/* Lab Option Details */}
                            {item.labOption && (
                              <div style={{ 
                                marginTop: '0.75rem', 
                                padding: '0.75rem', 
                                background: '#f9fafb', 
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb'
                              }}>
                                <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Lab Option:</p>
                                <p style={{ margin: 0, fontSize: '0.875rem', color: '#2C2C2C', fontWeight: '500' }}>
                                  {item.labOption.labName} - ৳{item.labOption.price?.toFixed(2) || '0.00'}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>No items in this order</p>
              )}
            </div>

            {/* Order Summary */}
            <div style={{ 
              background: 'linear-gradient(135deg, #f5f5f5 0%, #e5e5e5 100%)',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid #d3d3d3'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>Order Summary</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                  <span style={{ color: '#6b7280' }}>Subtotal:</span>
                  <span style={{ color: '#2C2C2C', fontWeight: '500' }}>৳{order.totalAmount?.toFixed(2) || '0.00'}</span>
                </div>
                {order.discount && order.discount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                    <span style={{ color: '#6b7280' }}>Discount:</span>
                    <span style={{ color: '#059669', fontWeight: '500' }}>-৳{order.discount.toFixed(2)}</span>
                  </div>
                )}
                {order.shippingCost && order.shippingCost > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                    <span style={{ color: '#6b7280' }}>Shipping:</span>
                    <span style={{ color: '#2C2C2C', fontWeight: '500' }}>৳{order.shippingCost.toFixed(2)}</span>
                  </div>
                )}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  paddingTop: '0.75rem',
                  borderTop: '2px solid #d3d3d3',
                  marginTop: '0.5rem'
                }}>
                  <span style={{ color: '#2C2C2C' }}>Total:</span>
                  <span style={{ color: '#dc2626' }}>৳{order.totalAmount?.toFixed(2) || '0.00'}</span>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            {order.shippingAddress && (
              <div style={{ 
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                marginTop: '1.5rem',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>Shipping Address</h3>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#2C2C2C', lineHeight: '1.6' }}>
                  {order.shippingAddress.street && `${order.shippingAddress.street}, `}
                  {order.shippingAddress.city && `${order.shippingAddress.city}, `}
                  {order.shippingAddress.state && `${order.shippingAddress.state}, `}
                  {order.shippingAddress.zipCode && `${order.shippingAddress.zipCode}, `}
                  {order.shippingAddress.country || 'Bangladesh'}
                </p>
              </div>
            )}

            {/* Close Button */}
            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                className="admin-btn admin-btn-primary"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p style={{ color: '#dc2626', fontSize: '0.875rem' }}>Failed to load order details</p>
            <button
              onClick={onClose}
              style={{
                marginTop: '1rem',
                padding: '0.75rem 1.5rem',
                background: '#f3f4f6',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMarketplace;
